import React, { useState, useEffect, useCallback } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Divider from "@mui/material/Divider";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";
import VuiBadge from "components/VuiBadge";
import LoadingSpinner from "components/LoadingSpinner";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { listClientProfiles, apiRequest } from "services/tenantUserAdminService";

import { IoRefresh, IoTrendingUp, IoTrendingDown } from "react-icons/io5";
import { FiBarChart2, FiTrendingUp, FiDollarSign, FiLayers } from "react-icons/fi";
import { MdAccountBalance, MdOutlineAccountTree } from "react-icons/md";
import { TC } from "../../tokens/text";

// ─── Theme constants ──────────────────────────────────────────────────────────

const CARD_BG = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.08)";

const selectSx = {};

const menuProps = {
  PaperProps: {
    sx: {
      background: "#0f1535",
      border: "1px solid rgba(226,232,240,0.3)",
      color: TC.primary,
      "& .MuiMenuItem-root:hover":        { background: "#f4f8ff" },
      "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,117,255,0.2)" },
    },
  },
};

// Account type colours
const TYPE_COLOR = {
  asset:    "#4ECDC4",
  liability:"#F7B731",
  equity:   "#0075FF",
  income:   "#01B574",
  expense:  "#FF6B6B",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtGBP(amount) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount ?? 0);
}

function fmtPct(pct) {
  if (pct == null || !isFinite(pct)) return "—";
  return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

function TypeBadge({ type }) {
  const color = TYPE_COLOR[type] || "rgba(255,255,255,0.3)";
  return (
    <span style={{
      background: `${color}18`, color,
      border: `1px solid ${color}44`,
      borderRadius: 4, padding: "1px 6px",
      fontSize: 10, fontWeight: 700, textTransform: "capitalize", letterSpacing: 0.3,
    }}>
      {type}
    </span>
  );
}

function AmountCell({ amount, bold, large, positive }) {
  const isPos  = positive !== undefined ? positive : (amount ?? 0) >= 0;
  const color  = bold
    ? (isPos ? "#01B574" : "#f44336")
    : "rgba(255,255,255,0.85)";
  return (
    <VuiTypography sx={{
      color, fontSize: large ? 18 : 13,
      fontWeight: bold ? 800 : 500,
      textAlign: "right", minWidth: 110,
      fontVariantNumeric: "tabular-nums",
    }}>
      {fmtGBP(amount)}
    </VuiTypography>
  );
}

function VarianceCell({ variance, variancePct }) {
  if (variance == null) return <VuiBox sx={{ minWidth: 130 }} />;
  const pos   = variance >= 0;
  const color = pos ? "#01B574" : "#f44336";
  return (
    <VuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} sx={{ minWidth: 130 }}>
      {pos ? <IoTrendingUp color={color} size={13} /> : <IoTrendingDown color={color} size={13} />}
      <VuiTypography sx={{ color, fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {fmtGBP(variance)}
      </VuiTypography>
      <VuiTypography sx={{ color: `${color}99`, fontSize: 11 }}>
        ({fmtPct(variancePct)})
      </VuiTypography>
    </VuiBox>
  );
}

// ─── Pill Tab Bar ─────────────────────────────────────────────────────────────

const TABS = [
  { label: "Trial Balance", icon: <MdOutlineAccountTree size={15} /> },
  { label: "Profit & Loss", icon: <FiTrendingUp size={15} /> },
  { label: "Balance Sheet", icon: <MdAccountBalance size={15} /> },
];

function PillTabs({ active, onChange }) {
  return (
    <VuiBox display="flex" gap={0.5} p="4px"
      sx={{ background: "#3f6489", borderRadius: "14px", border: "1px solid #d8e6f7", width: "fit-content" }}>
      {TABS.map((t, i) => {
        const isActive = active === i;
        return (
          <VuiBox key={t.label}
            onClick={() => onChange(i)}
            display="flex" alignItems="center" gap={0.8}
            sx={{
              px: "18px", py: "8px", borderRadius: "10px", cursor: "pointer",
              background: isActive ? "linear-gradient(135deg, #0075FF 0%, #00BFFF 100%)" : "transparent",
              boxShadow: isActive ? "0 4px 15px rgba(0,117,255,0.4)" : "none",
              transition: "all 0.2s ease",
              "&:hover": { background: isActive ? "linear-gradient(135deg, #0075FF 0%, #00BFFF 100%)" : "rgba(255,255,255,0.08)" },
            }}>
            <span style={{ color: isActive ? "#fff" : "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", fontSize: 15 }}>
              {t.icon}
            </span>
            <VuiTypography sx={{ color: isActive ? "#fff" : "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: isActive ? 700 : 500, whiteSpace: "nowrap", letterSpacing: isActive ? 0.3 : 0 }}>
              {t.label}
            </VuiTypography>
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}

// ─── Raw HTML table styles (no MUI interference) ─────────────────────────────

const TH = {
  color: TC.secondary, fontSize: "11px", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.6px",
  background: "rgba(15,20,50,0.85)",
  borderBottom: "1px solid #e4eef8",
  borderRight: "1px solid rgba(255,255,255,0.12)",
  padding: "10px 16px", textAlign: "left", whiteSpace: "nowrap",
};
const TD = {
  color: TC.primary, fontSize: "13px",
  borderBottom: "1px solid #e4eef8",
  borderRight: "1px solid rgba(255,255,255,0.08)",
  padding: "9px 16px", textAlign: "left",
};
const GRP = {
  fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.5px", padding: "7px 16px", borderBottom: "none",
};
const TOT = {
  fontSize: "13px", fontWeight: 800, color: TC.primary,
  background: "#f4f8ff",
  borderTop: "1px solid #e4eef8",
  borderBottom: "1px solid #e4eef8",
  borderRight: "1px solid rgba(255,255,255,0.08)",
  padding: "11px 16px",
};

function Tbl({ cols, children, style }) {
  return (
    <div style={{ background: "rgba(10,14,35,0.6)", borderRadius: "12px", overflow: "hidden", border: "1px solid #d8e6f7", ...style }}>
      <table aria-label="Financial report table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
        {cols && <colgroup>{cols.map((w, i) => <col key={i} width={w} />)}</colgroup>}
        {children}
      </table>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ label }) {
  return (
    <VuiBox py={5} display="flex" flexDirection="column" alignItems="center" gap={1}>
      <FiBarChart2 color="rgba(255,255,255,0.15)" size={40} />
      <VuiTypography sx={{ color: TC.secondary, fontSize: 14 }}>{label}</VuiTypography>
    </VuiBox>
  );
}

// ─── Trial Balance ────────────────────────────────────────────────────────────

function TrialBalance({ data }) {
  if (!data?.lines?.length) return <EmptyState label="No ledger entries for this period." />;
  const lines   = data.lines.filter(r => r.account !== "TOTAL");
  const totals  = data.lines.find(r => r.account === "TOTAL");
  const balanced = Math.abs((totals?.totalDebits ?? 0) - (totals?.totalCredits ?? 0)) < 0.01;
  const groups  = {};
  lines.forEach(r => { const t = r.type || "other"; if (!groups[t]) groups[t] = []; groups[t].push(r); });

  return (
    <Tbl cols={["38%","12%","16%","16%","18%"]}>
      <thead>
        <tr>
          <th style={TH}>Account</th>
          <th style={TH}>Type</th>
          <th style={{ ...TH, textAlign: "right" }}>Debits</th>
          <th style={{ ...TH, textAlign: "right" }}>Credits</th>
          <th style={{ ...TH, textAlign: "right" }}>Balance</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(groups).map(([type, rows]) => (
          <React.Fragment key={type}>
            <tr>
              <td colSpan={5} style={{ ...GRP, color: TYPE_COLOR[type] || "#aaa", background: `${TYPE_COLOR[type] || "#666"}18`, boxShadow: `inset 4px 0 0 ${TYPE_COLOR[type] || "#666"}` }}>
                {type}
              </td>
            </tr>
            {rows.map((row, i) => (
              <tr key={i}>
                <td style={{ ...TD, paddingLeft: "28px" }}>{row.label || row.account}</td>
                <td style={TD}><TypeBadge type={row.type} /></td>
                <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(row.totalDebits)}</td>
                <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(row.totalCredits)}</td>
                <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums", color: row.balance >= 0 ? "rgba(255,255,255,0.9)" : "#f44336", fontWeight: 600 }}>{fmtGBP(row.balance)}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
        {totals && (
          <tr>
            <td style={TOT}>TOTALS</td>
            <td style={TOT} />
            <td style={{ ...TOT, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(totals.totalDebits)}</td>
            <td style={{ ...TOT, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(totals.totalCredits)}</td>
            <td style={{ ...TOT, textAlign: "right" }}>
              <span style={{ background: balanced ? "rgba(1,181,116,0.18)" : "rgba(255,71,87,0.18)", color: balanced ? "#01B574" : "#f44336", border: `1px solid ${balanced ? "rgba(1,181,116,0.4)" : "rgba(255,71,87,0.4)"}`, borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                {balanced ? "✓ Balanced" : "⚠ Imbalanced"}
              </span>
            </td>
          </tr>
        )}
      </tbody>
    </Tbl>
  );
}

// ─── P&L ──────────────────────────────────────────────────────────────────────

function PLSectionRows({ title, accentColor, rows, total, totalLabel, hasComparison }) {
  if (!rows?.length) return null;
  return (
    <React.Fragment>
      <tr>
        <td colSpan={hasComparison ? 4 : 2} style={{ ...GRP, color: accentColor, background: `${accentColor}18`, boxShadow: `inset 4px 0 0 ${accentColor}` }}>
          {title}
        </td>
      </tr>
      {rows.map((row, i) => (
        <tr key={i}>
          <td style={{ ...TD, paddingLeft: "28px" }}>{row.label || row.account}</td>
          <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(row.amount)}</td>
          {hasComparison && <>
            <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums", color: TC.secondary }}>{row.priorAmount != null ? fmtGBP(row.priorAmount) : "—"}</td>
            <td style={{ ...TD, textAlign: "right" }}>
              {row.variance != null && (
                <span style={{ color: row.variance >= 0 ? "#01B574" : "#f44336", fontWeight: 700, fontSize: 12 }}>
                  {row.variance >= 0 ? "▲" : "▼"} {fmtGBP(Math.abs(row.variance))}
                </span>
              )}
            </td>
          </>}
        </tr>
      ))}
      {total != null && (
        <tr>
          <td style={{ ...TD, fontWeight: 700, color: accentColor, background: `${accentColor}0A` }}>{totalLabel}</td>
          <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: accentColor, background: `${accentColor}0A` }}>{fmtGBP(total)}</td>
          {hasComparison && <>
            <td style={{ ...TD, background: `${accentColor}0A` }} />
            <td style={{ ...TD, background: `${accentColor}0A` }} />
          </>}
        </tr>
      )}
    </React.Fragment>
  );
}

function PLSubtotal({ label, amount, hasComparison }) {
  const color = amount >= 0 ? "#01B574" : "#f44336";
  return (
    <tr>
      <td style={{ ...TOT, color, fontSize: "14px" }}>{label}</td>
      <td style={{ ...TOT, textAlign: "right", fontVariantNumeric: "tabular-nums", color, fontSize: "15px" }}>{fmtGBP(amount)}</td>
      {hasComparison && <><td style={TOT} /><td style={TOT} /></>}
    </tr>
  );
}

function PandL({ data, hasComparison }) {
  if (!data) return <EmptyState label="No P&L data for this period." />;
  return (
    <Tbl cols={hasComparison ? ["52%","16%","16%","16%"] : ["70%","30%"]}>
      <thead>
        <tr>
          <th style={TH}>Description</th>
          <th style={{ ...TH, textAlign: "right" }}>Current Period</th>
          {hasComparison && <>
            <th style={{ ...TH, textAlign: "right" }}>Prior Period</th>
            <th style={{ ...TH, textAlign: "right" }}>Variance</th>
          </>}
        </tr>
      </thead>
      <tbody>
        <PLSectionRows title="Revenue"            accentColor="#01B574" rows={data.income?.lines}            total={data.income?.total}            totalLabel="Total Revenue"  hasComparison={hasComparison} />
        <PLSectionRows title="Cost of Sales"      accentColor="#F7B731" rows={data.costOfSales?.lines}       total={data.costOfSales?.total}       totalLabel="Total COGS"     hasComparison={hasComparison} />
        <PLSubtotal label="Gross Profit"          amount={data.grossProfit}  hasComparison={hasComparison} />
        <PLSectionRows title="Operating Expenses" accentColor="#FF6B6B" rows={data.operatingExpenses?.lines} total={data.operatingExpenses?.total} totalLabel="Total Expenses" hasComparison={hasComparison} />
        <PLSubtotal label="Net Profit / (Loss)"   amount={data.netProfit}    hasComparison={hasComparison} />
      </tbody>
    </Tbl>
  );
}

// ─── Balance Sheet ────────────────────────────────────────────────────────────

function BSSection({ title, accentColor, rows, total }) {
  return (
    <VuiBox mb={3}>
      <Tbl cols={["70%","30%"]}>
        <thead>
          <tr>
            <th style={{ ...TH, color: accentColor, boxShadow: `inset 4px 0 0 ${accentColor}` }}>{title}</th>
            <th style={{ ...TH, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row, i) => (
            <tr key={i}>
              <td style={{ ...TD, paddingLeft: "20px" }}>{row.label || row.account}</td>
              <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtGBP(row.balance)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...TOT, color: accentColor }}>Total {title}</td>
            <td style={{ ...TOT, textAlign: "right", fontVariantNumeric: "tabular-nums", color: accentColor }}>{fmtGBP(total)}</td>
          </tr>
        </tbody>
      </Tbl>
    </VuiBox>
  );
}

function BalanceSheet({ data }) {
  if (!data) return <EmptyState label="No balance sheet data." />;

  return (
    <VuiBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <BSSection title="Assets"      accentColor="#4ECDC4" rows={data.assets}      total={data.totalAssets} />
        </Grid>
        <Grid item xs={12} md={6}>
          <BSSection title="Liabilities" accentColor="#F7B731" rows={data.liabilities} total={data.totalLiabilities} />
          <BSSection title="Equity"      accentColor="#0075FF" rows={data.equity}      total={data.totalEquity} />
        </Grid>
      </Grid>
      <VuiBox mt={1} px={2} py="13px"
        sx={{ background: data.balanced ? "rgba(1,181,116,0.08)" : "rgba(255,71,87,0.08)", border: `1px solid ${data.balanced ? "rgba(1,181,116,0.3)" : "rgba(255,71,87,0.3)"}`, borderRadius: 2 }}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <VuiTypography sx={{ color: data.balanced ? "#01B574" : "#f44336", fontSize: 14, fontWeight: 800 }}>
            {data.balanced ? "✓ Balance sheet balances" : "⚠ Balance sheet does not balance"}
          </VuiTypography>
          <VuiBox display="flex" gap={3}>
            {[{ label: "Total Assets", value: data.totalAssets }, { label: "Liabilities + Equity", value: (data.totalLiabilities || 0) + (data.totalEquity || 0) }].map(({ label, value }) => (
              <VuiBox key={label} textAlign="right">
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</VuiTypography>
                <VuiTypography sx={{ color: TC.primary, fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(value)}</VuiTypography>
              </VuiBox>
            ))}
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </VuiBox>
  );
}

// ─── KPI Summary Strip ────────────────────────────────────────────────────────

function KpiStrip({ pl, bs, loading }) {
  const netProfit   = pl?.netProfit   ?? null;
  const totalIncome = pl?.income?.total ?? null;
  const totalExpenses = pl?.operatingExpenses?.total ?? null;
  const totalAssets = bs?.totalAssets  ?? null;

  return (
    <Grid container spacing={2} mb={3}>
      {[
        {
          title: "Net Profit",
          count: loading || netProfit == null ? "—" : fmtGBP(netProfit),
          pct: { color: netProfit >= 0 ? "success" : "error", text: netProfit >= 0 ? "profitable" : "loss" },
          icon: <FiTrendingUp size="20px" color="white" />,
        },
        {
          title: "Total Revenue",
          count: loading || totalIncome == null ? "—" : fmtGBP(totalIncome),
          pct: { color: "info", text: "this period" },
          icon: <FiDollarSign size="20px" color="white" />,
        },
        {
          title: "Total Expenses",
          count: loading || totalExpenses == null ? "—" : fmtGBP(totalExpenses),
          pct: { color: "warning", text: "operating costs" },
          icon: <FiBarChart2 size="20px" color="white" />,
        },
        {
          title: "Total Assets",
          count: loading || totalAssets == null ? "—" : fmtGBP(totalAssets),
          pct: { color: "info", text: "on balance sheet" },
          icon: <FiLayers size="20px" color="white" />,
        },
      ].map(({ title, count, pct, icon }) => (
        <Grid item xs={12} sm={6} xl={3} key={title}>
          <MiniStatisticsCard
            title={{ text: title, fontWeight: "regular" }}
            count={count}
            percentage={pct}
            icon={{ color: "info", component: icon }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

// ─── Demo / fallback data ─────────────────────────────────────────────────────

const DEMO_TB = {
  lines: [
    { account: "1200", label: "Bank Current Account", type: "asset",     totalDebits: 45200, totalCredits: 12800, balance: 32400 },
    { account: "1100", label: "Accounts Receivable",  type: "asset",     totalDebits: 18500, totalCredits:  6000, balance: 12500 },
    { account: "2100", label: "Accounts Payable",     type: "liability", totalDebits:  4200, totalCredits:  9800, balance: -5600 },
    { account: "2201", label: "VAT Liability",        type: "liability", totalDebits:  1200, totalCredits:  3600, balance: -2400 },
    { account: "3000", label: "Share Capital",        type: "equity",    totalDebits:     0, totalCredits: 20000, balance:-20000 },
    { account: "4000", label: "Sales Revenue",        type: "income",    totalDebits:     0, totalCredits: 48000, balance:-48000 },
    { account: "7000", label: "Staff Wages",          type: "expense",   totalDebits: 14400, totalCredits:     0, balance: 14400 },
    { account: "7100", label: "Rent & Rates",         type: "expense",   totalDebits:  6000, totalCredits:     0, balance:  6000 },
    { account: "7200", label: "Utilities",            type: "expense",   totalDebits:  1800, totalCredits:     0, balance:  1800 },
    { account: "7400", label: "Software & Subscriptions", type: "expense", totalDebits: 2400, totalCredits:  0, balance:  2400 },
    { account: "TOTAL", label: "TOTAL", totalDebits: 93700, totalCredits: 100200, balance: 6500 },
  ],
};

const DEMO_PL = {
  income:            { lines: [{ account: "4000", label: "Sales Revenue", amount: 48000, priorAmount: 41000, variance: 7000, variancePct: 17.1 }], total: 48000 },
  costOfSales:       { lines: [{ account: "5000", label: "Cost of Goods Sold", amount: 12000, priorAmount: 10500, variance: 1500, variancePct: 14.3 }], total: 12000 },
  grossProfit: 36000,
  operatingExpenses: {
    lines: [
      { account: "7000", label: "Staff Wages",              amount: 14400, priorAmount: 13200, variance: 1200, variancePct: 9.1 },
      { account: "7100", label: "Rent & Rates",             amount:  6000, priorAmount:  6000, variance:    0, variancePct: 0   },
      { account: "7200", label: "Utilities",                amount:  1800, priorAmount:  1950, variance: -150, variancePct:-7.7 },
      { account: "7400", label: "Software & Subscriptions", amount:  2400, priorAmount:  2100, variance:  300, variancePct:14.3 },
    ],
    total: 24600,
  },
  netProfit: 11400,
};

const DEMO_BS = {
  assets:           [{ account: "1200", label: "Bank Current Account", balance: 32400 }, { account: "1100", label: "Accounts Receivable", balance: 12500 }],
  totalAssets:      44900,
  liabilities:      [{ account: "2100", label: "Accounts Payable", balance: 5600 }, { account: "2201", label: "VAT Liability", balance: 2400 }],
  totalLiabilities: 8000,
  equity:           [{ account: "3000", label: "Share Capital", balance: 20000 }, { account: "3100", label: "Retained Earnings", balance: 16900 }],
  totalEquity:      36900,
  balanced:         true,
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Reports() {
  const { tenantId } = useAuthz();
  const { pinnedClientId, pinClient } = useClient() || {};

  const [profiles, setProfiles]             = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [tab, setTab]                       = useState(0);
  const [loading, setLoading]               = useState(false);
  const [data, setData] = useState({ tb: DEMO_TB, pl: DEMO_PL, bs: DEMO_BS });
  const [error, setError]                   = useState("");

  const now           = new Date();
  const currentPeriod = now.toISOString().slice(0, 7);
  const priorPeriod   = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 7);
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

  const periodOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toISOString().slice(0, 7);
  });

  useEffect(() => {
    if (!tenantId) return;
    listClientProfiles({ tenantId, status: "active" })
      .then(d => {
        const list = d.clientProfiles || [];
        setProfiles(list);
        const nextSelected =
          (pinnedClientId && list.some((client) => client.id === pinnedClientId) && pinnedClientId) ||
          list[0]?.id ||
          "";
        setSelectedClient(nextSelected);
        if (nextSelected && nextSelected !== pinnedClientId) {
          const match = list.find((client) => client.id === nextSelected);
          pinClient?.(nextSelected, match?.legalName || match?.tradingName || nextSelected);
        }
      }).catch(() => {});
  }, [tenantId, pinnedClientId, pinClient]);

  const loadReports = useCallback(async () => {
    if (!tenantId || !selectedClient) return;
    setLoading(true); setError("");
    try {
      const base = `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/reports`;
      const [tb, pl, bs] = await Promise.all([
        apiRequest(`${base}/trial-balance`, { query: { period: selectedPeriod } }).catch(() => null),
        apiRequest(`${base}/pl`,            { query: { period: selectedPeriod, compare: priorPeriod } }).catch(() => null),
        apiRequest(`${base}/balance-sheet`, { query: { period: selectedPeriod } }).catch(() => null),
      ]);
      setData({ tb: tb || DEMO_TB, pl: pl || DEMO_PL, bs: bs || DEMO_BS });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [tenantId, selectedClient, selectedPeriod]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const hasComparison = !!data.pl?.income;
  const client = profiles.find(p => p.id === selectedClient);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>📊 Financial Reports</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13, mt: 0.3 }}>
              {client ? `${client.legalName || client.tradingName} · ` : ""}
              Trial balance, P&amp;L, and balance sheet
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Client</InputLabel>
              <Select value={selectedClient} onChange={e => {
                const next = e.target.value;
                setSelectedClient(next);
                const match = profiles.find((client) => client.id === next);
                pinClient?.(next, match?.legalName || match?.tradingName || next);
              }}
                label="Client" sx={selectSx} MenuProps={menuProps}>
                {profiles.map(p => (
                  <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>
                    {p.legalName || p.tradingName || p.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 130 }}>
              <InputLabel>Period</InputLabel>
              <Select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
                label="Period" sx={selectSx} MenuProps={menuProps}>
                {periodOptions.map(p => (
                  <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <VuiButton size="small" color="info" variant="outlined"
              startIcon={<IoRefresh size={14} />} onClick={loadReports}
              sx={{ fontSize: 12, bordercolor: "#7a94b0", height: 38 }}>
              Refresh
            </VuiButton>
          </VuiBox>
        </VuiBox>

        {error && (
          <VuiBox mb={3} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "12px 16px" }}>
            <VuiTypography sx={{ color: "#f44336", fontSize: 13 }}>{error}</VuiTypography>
          </VuiBox>
        )}

        {/* ── KPI strip ───────────────────────────────────────────────────── */}
        <KpiStrip pl={data.pl} bs={data.bs} loading={loading} />

        {/* ── Tabs + report body ───────────────────────────────────────────── */}
        <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "20px", overflow: "visible" }}>
          <VuiBox p={3}>
            {/* Tab bar + comparison note */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
              <PillTabs active={tab} onChange={setTab} />
              {tab === 1 && (
                <VuiBox display="flex" alignItems="center" gap={0.8} sx={{ background: "rgba(0,117,255,0.08)", border: "1px solid rgba(0,117,255,0.2)", borderRadius: "8px", px: 1.5, py: 0.8 }}>
                  <VuiTypography sx={{ color: "rgba(0,117,255,0.9)", fontSize: 12 }}>
                    Comparing <strong>{selectedPeriod}</strong> vs <strong>{priorPeriod}</strong>
                  </VuiTypography>
                </VuiBox>
              )}
            </VuiBox>

            <Divider sx={{ bordercolor: "#7a94b0", mb: 3 }} />

            {/* Report content */}
            {loading ? (
              <VuiBox py={5} display="flex" justifyContent="center">
                <LoadingSpinner label={`Loading ${TABS[tab].label}…`} />
              </VuiBox>
            ) : (
              <>
                {tab === 0 && <TrialBalance data={data.tb} />}
                {tab === 1 && <PandL data={data.pl} hasComparison={hasComparison} />}
                {tab === 2 && <BalanceSheet data={data.bs} />}
              </>
            )}
          </VuiBox>
        </Card>

      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

